<template>
    <div class="pdf-slides" ref="frame">
        <canvas ref="canvas" v-show="ready"></canvas>
        <div v-if="!ready && !failed" class="pdf-slides-wait"><span class="spinner-border"></span></div>
        <div v-if="failed" class="pdf-slides-wait">No se ha podido cargar la presentación.</div>
        <div v-if="pages > 1" class="pdf-slides-counter">{{ shown_page + 1 }} / {{ pages }}</div>
    </div>
</template>

<script>
import { loadPdf } from '@/utils/pdf'

// A presentation (utils.ACTIVITY_TYPE.PRESENTATION): one page of the PDF at
// a time inside a 16:9 frame, the page being decided elsewhere (the room's
// game.content_page, moved by the presenter - see server.js's
// _presentation_step - or the player's own arrows online).
export default {
    name: "PdfSlides",
    emits: ['pages'],
    props: {
        url: { type: String, required: true },
        page: { type: Number, default: 0 },
    },
    data(){
        return {
            ready: false,
            failed: false,
            pages: 0,
            shown_page: 0,
        };
    },
    watch: {
        url(){ this.open(); },
        page(){ this.render(); },
    },
    mounted(){
        this.resize_observer = new ResizeObserver(() => {
            cancelAnimationFrame(this.resize_frame);
            this.resize_frame = requestAnimationFrame(this.render);
        });
        this.resize_observer.observe(this.$refs.frame);
        this.open();
    },
    beforeUnmount(){
        this.resize_observer.disconnect();
        cancelAnimationFrame(this.resize_frame);
        this.cancel_render();
        if(this.doc) this.doc.destroy();
        this.doc = null;
    },
    methods: {
        async open(){
            this.cancel_render();
            if(this.doc) this.doc.destroy();
            this.doc = null;
            this.ready = false;
            this.failed = false;
            const url = this.url;
            try{
                const doc = await loadPdf(url);
                if(url != this.url || !this.$refs.frame){ doc.destroy(); return; }
                this.doc = doc;
                this.pages = doc.numPages;
                this.$emit('pages', doc.numPages);
                await this.render();
            }catch(ex){
                console.warn('No se ha podido cargar el PDF:', ex);
                this.failed = true;
            }
        },
        cancel_render(){
            if(this.task) this.task.cancel();
            this.task = null;
        },
        // Fits the whole page inside the frame, drawn at the screen's real
        // pixel density so text stays crisp on a projector or a phone.
        async render(){
            const doc = this.doc;
            const frame = this.$refs.frame;
            if(!doc || !frame) return;
            // Page turns can arrive faster than pages load - only the
            // latest request gets drawn.
            const seq = this.render_seq = (this.render_seq || 0) + 1;
            const number = Math.min(Math.max(0, this.page), doc.numPages - 1);
            const page = await doc.getPage(number + 1);
            if(doc !== this.doc || seq !== this.render_seq) return;

            const base = page.getViewport({ scale: 1 });
            const scale = Math.min(frame.clientWidth / base.width, frame.clientHeight / base.height) || 1;
            const ratio = window.devicePixelRatio || 1;
            const viewport = page.getViewport({ scale: scale * ratio });
            const canvas = this.$refs.canvas;

            this.cancel_render();
            // Drawn offscreen first: swapping a finished bitmap in avoids
            // flashing a blank slide on every page turn.
            const buffer = document.createElement('canvas');
            buffer.width = Math.floor(viewport.width);
            buffer.height = Math.floor(viewport.height);
            const task = page.render({ canvasContext: buffer.getContext('2d'), viewport });
            this.task = task;
            try{
                await task.promise;
            }catch(ex){
                if(ex && ex.name == 'RenderingCancelledException') return;
                throw ex;
            }
            if(this.task !== task) return;
            this.task = null;

            canvas.width = buffer.width;
            canvas.height = buffer.height;
            canvas.style.width = Math.floor(viewport.width / ratio) + 'px';
            canvas.style.height = Math.floor(viewport.height / ratio) + 'px';
            canvas.getContext('2d').drawImage(buffer, 0, 0);
            this.shown_page = number;
            this.ready = true;
        }
    }
}
</script>

<style scoped>
    /* 16:9 whatever the screen: as wide as it can be while still fitting
       70vh of height. A slide with another ratio is letterboxed inside. */
    .pdf-slides{
        position: relative;
        width: min(100%, calc(70vh * 16 / 9));
        aspect-ratio: 16 / 9;
        margin: 0 auto;
        border-radius: 8px;
        background-color: #111;
        color: #fff;
        display: flex;
        align-items: center;
        justify-content: center;
        overflow: hidden;
    }
    .pdf-slides canvas{
        background-color: #fff;
    }
    .pdf-slides-wait{
        font-family: "Archivo", sans-serif;
        font-weight: 700;
        text-align: center;
    }
    .pdf-slides-counter{
        position: absolute;
        right: 12px;
        bottom: 12px;
        padding: 2px 10px;
        border-radius: 999px;
        background-color: rgba(0, 0, 0, 0.6);
        color: #fff;
        font-family: "Archivo", sans-serif;
        font-weight: 700;
        font-size: 0.85rem;
    }
</style>
